import {
  inferAdditionalFields,
  organizationClient,
  twoFactorClient,
} from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

/**
 * Better Auth's browser client for the endpoints it owns (/api/auth/*). Everything the api
 * owns (/api/v1/*) goes through the generated client in packages/contract. Same origin via
 * the edge (or the Vite proxy), so cookies just work.
 */
export const authClient = createAuthClient({
  baseURL: window.location.origin,
  basePath: "/api/auth",
  // Resolve fetch at call time so test interceptors (MSW) installed after import still apply.
  fetchOptions: { customFetchImpl: (input, init) => globalThis.fetch(input, init) },
  plugins: [
    twoFactorClient(),
    organizationClient(),
    inferAdditionalFields({
      user: {
        locale: { type: "string", required: false },
        promotionCode: { type: "string", required: false },
        // Sign-up only: the server names the organization with it and keeps it off the user.
        companyName: { type: "string", required: false },
        isPlatformAdmin: { type: "boolean", required: false, input: false },
      },
    }),
  ],
});

export type AuthClient = typeof authClient;

/** Better Auth answers `{ data, error }`; this narrows the error into what the UI shows. */
export interface AuthClientError {
  readonly status: number;
  readonly code: string | undefined;
  readonly message: string | undefined;
}

export function toAuthError(error: unknown): AuthClientError {
  if (error && typeof error === "object") {
    const e = error as { status?: unknown; code?: unknown; message?: unknown };
    return {
      status: typeof e.status === "number" ? e.status : 0,
      code: typeof e.code === "string" ? e.code : undefined,
      message: typeof e.message === "string" ? e.message : undefined,
    };
  }
  return { status: 0, code: undefined, message: undefined };
}
